import React, { ReactNode, useState, useEffect } from 'react';
//CSS
import styles from './Button.module.css';

type ButtonProps = {
    id?: string;
    clickHandler?: () => void;
    className?: string;
    border?: string;
    children?: ReactNode;
    colour?: string | null;
    textColour?: string | null;
    size?: "Small" | "Icon" | "IconSmall" | null;
    autoDisable?: boolean; // Time in milliseconds
    style?: React.CSSProperties;
    type?: "button" | "submit" | "reset";
    disabled?: boolean; // Allow external control of disabled state
    /** When true, skip the default hover colour (parent handles hover styling). */
    suppressHover?: boolean;
};

const Button = ({
    id,
    border,
    clickHandler,
    className,
    children,
    colour,
    textColour,
    size,
    autoDisable,
    style,
    type = "button",
    disabled,
    suppressHover = false,
    
}: ButtonProps) => {
    const [isDisabled, setIsDisabled] = useState(false);
    const [hovered, setHovered] = useState(false);

    const tstyle = {
        ...(style || {}),
        ...(colour && { backgroundColor: colour }),
        ...(textColour && { color: textColour }),
        ...(border && { border: `1px solid ${border}` }),
    };

    // Handle button click with auto-disable
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        // If there's a clickHandler, stop propagation and call it
        // Otherwise, let the parent (e.g., Link) handle the event
        if (clickHandler) {
            event.stopPropagation();
            event.preventDefault(); // Prevent default when button has its own handler
            clickHandler();
        } else {
            // No clickHandler - don't prevent default or stop propagation
            // This allows parent Link components to handle navigation
            // Only prevent default for submit/reset types to avoid form submission
            if (type === "submit" || type === "reset") {
                event.preventDefault();
            }
        }

        if (autoDisable) {
            setIsDisabled(true); // Disable the button
            setTimeout(() => {
                setIsDisabled(false); // Re-enable after autoDisable duration
            }, 3500);
        }
    };

    return (
        <button 
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            type={type}
            onClick={handleClick}
            disabled={isDisabled || disabled}
            className={
                `${styles.container} ${hovered && !suppressHover ? styles.hovered : ""} ${size === "Small" ? styles.small : size === "Icon" ? styles.icon : size === "IconSmall" ? styles.iconSmall : ""} ${
                    isDisabled || disabled ? styles.disabled : ""
                } ${className || ""}`
            }
            style={tstyle}
            id={id}
        >
            {children}
        </button>
    );
};

export default Button;
